import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/contexts/AuthContext';
import { getHomeRoute } from '@/lib/api';
import { enterFullscreenWithEscapeLock } from '@/lib/fullscreenUtils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/card';
import { toast } from 'sonner';
import { ShieldCheck, Loader2, RefreshCw } from 'lucide-react';

const _decodeJwtPayload = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return {};
  }
};

const MFAVerify = () => {
  const navigate  = useNavigate();
  const { login } = useAuth();

  const [code,       setCode]       = useState('');
  const [loading,    setLoading]    = useState(false);
  const [factorId,   setFactorId]   = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [timer,      setTimer]      = useState(30);
  const [preparing,  setPreparing]  = useState(true);
  const intervalRef  = useRef(null);

  const createChallenge = useCallback(async (fId) => {
    const { data, error } = await supabase.auth.mfa.challenge({ factorId: fId });
    if (error) throw error;
    setChallengeId(data.id);
    // Countdown timer resets on each new challenge
    setTimer(30);
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!data?.session) { navigate('/login'); return; }

        const { data: factors, error } = await supabase.auth.mfa.listFactors();
        if (error) throw error;
        const totp = (factors?.totp || []).find(f => f.status === 'verified');
        if (!totp) {
          // Not enrolled yet — send to enroll
          navigate('/mfa/enroll', { replace: true });
          return;
        }
        setFactorId(totp.id);
        await createChallenge(totp.id);
      } catch (e) {
        toast.error('Could not start verification: ' + (e.message || 'Unknown error'));
      } finally {
        setPreparing(false);
      }
    };
    init();
  }, [navigate, createChallenge]);

  // Countdown to next TOTP window
  useEffect(() => {
    if (preparing) return;
    intervalRef.current = setInterval(() => {
      setTimer(t => {
        if (t <= 1) {
          // Auto-refresh challenge when TOTP window rolls over
          if (factorId) createChallenge(factorId).catch(() => {});
          return 30;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [preparing, factorId, createChallenge]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { toast.error('Enter the 6-digit code from your authenticator app'); return; }
    setLoading(true);
    try {
      const { data: verified, error } = await supabase.auth.mfa.verify({
        factorId,
        challengeId,
        code: code.trim(),
      });
      if (error) throw error;

      // auth-js v2.x returns the session fields directly on data (not nested under .session)
      const aal2Token    = verified.access_token;
      const refreshToken = verified.refresh_token;
      localStorage.setItem('kiranaflow_token', aal2Token);
      localStorage.setItem('kiranaflow_refresh_token', refreshToken);

      const pendingUser = JSON.parse(sessionStorage.getItem('kiranaflow_pending_user') || 'null');
      sessionStorage.removeItem('kiranaflow_pending_user');
      sessionStorage.removeItem('kiranaflow_token'); // aal1 gate token — aal2 is now in localStorage

      const payload = _decodeJwtPayload(aal2Token);
      const userData = pendingUser || {
        id: payload.sub, role: payload.role, owner_id: payload.owner_id,
        store_id: payload.store_id, owner_name: '', shop_name: '',
        mobile: '', membership_tier: payload.membership_tier || 'starter',
      };

      await login({ token: aal2Token, refresh_token: refreshToken, user: userData });

      navigate(getHomeRoute(userData.role), { replace: true });
      enterFullscreenWithEscapeLock();
    } catch (e) {
      toast.error(e.message?.includes('Invalid') ? 'Incorrect code — check your app and try again' : (e.message || 'Verification failed'));
      setCode('');
      // Refresh challenge after failed attempt
      if (factorId) createChallenge(factorId).catch(() => {});
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-amber-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-sm shadow-lg">
        <CardHeader className="space-y-3 text-center">
          <div className="mx-auto bg-primary text-primary-foreground w-14 h-14 rounded-full flex items-center justify-center">
            <ShieldCheck className="h-7 w-7" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Two-Factor Verification</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Open your authenticator app and enter the current 6-digit code.
            </p>
          </div>
        </CardHeader>

        <CardContent>
          {preparing ? (
            <div className="flex items-center justify-center py-10 gap-3 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" /> Loading…
            </div>
          ) : (
            <form onSubmit={handleVerify} className="space-y-4">
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Authenticator code</label>
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <RefreshCw className="h-3 w-3" /> {timer}s
                  </span>
                </div>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="123456"
                  value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                  className="h-14 text-center text-2xl font-mono tracking-widest"
                  autoFocus
                  autoComplete="one-time-code"
                />
              </div>
              <Button type="submit" className="w-full h-12 text-base font-bold gap-2" disabled={loading || code.length !== 6}>
                {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Verifying…</> : 'Verify'}
              </Button>
            </form>
          )}
        </CardContent>

        <CardFooter className="justify-center">
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => { localStorage.removeItem('kiranaflow_token'); localStorage.removeItem('kiranaflow_refresh_token'); navigate('/login'); }}
          >
            ← Back to sign in
          </button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default MFAVerify;
